// =====================================================
// CONDITION DRAFTS
// Editable (possibly incomplete) form rows <-> stored PricingCondition
// =====================================================

import { formatCondition, isSetCondition, type ConditionLookups } from '@/models/pricingRule';
import type { ConditionType, PricingActionType, PricingCondition } from '@/types/pricing.types';

/** Criteria offered in the form (EARLY_BIRD / GUEST_TYPE only exist as legacy data) */
export type DraftKind =
  | 'PARTICIPANT_TYPE'
  | 'MINISTER_DEGREE'
  | 'DISTRICT'
  | 'REGION'
  | 'ROLE'
  | 'MINISTER_AGE'
  | 'REGISTERED_BEFORE'
  | 'PARTICIPANT_COUNT';

export const DRAFT_KINDS: { kind: DraftKind; label: string; hint: string }[] = [
  { kind: 'PARTICIPANT_TYPE', label: 'Tipo de participante', hint: 'Ministro, delegado o invitado' },
  { kind: 'MINISTER_DEGREE', label: 'Grado ministerial', hint: 'Ordenado, licenciado…' },
  { kind: 'DISTRICT', label: 'Distrito', hint: 'Distrito del ministro' },
  { kind: 'REGION', label: 'Región', hint: 'Región del ministro' },
  { kind: 'ROLE', label: 'Cargo', hint: 'Superintendente, presbítero, pastor…' },
  { kind: 'MINISTER_AGE', label: 'Edad', hint: 'Edad del ministro al inicio del evento' },
  { kind: 'REGISTERED_BEFORE', label: 'Inscrito hasta una fecha', hint: 'Inscripción anticipada' },
  { kind: 'PARTICIPANT_COUNT', label: 'Primeros inscritos', hint: 'Los primeros N en inscribirse' },
];

export interface ConditionDraft {
  key: string;
  /** undefined until the user picks a criterion */
  kind?: DraftKind;
  /** set criteria: "no es" instead of "es" */
  negate: boolean;
  values: string[];
  min: string;
  max: string;
  date: string;
  count: string;
  /** stored condition this form can't edit (old versions) — kept untouched */
  legacy?: PricingCondition;
}

let seq = 0;
const nextKey = () => `c${Date.now().toString(36)}${seq++}`;

export function newDraft(kind?: DraftKind, patch: Partial<ConditionDraft> = {}): ConditionDraft {
  return { key: nextKey(), kind, negate: false, values: [], min: '', max: '', date: '', count: '', ...patch };
}

const toNum = (v: unknown) => (typeof v === 'number' ? v : Number(v));

export function draftFromCondition(c: PricingCondition): ConditionDraft {
  const legacy = () => newDraft(undefined, { legacy: c });
  const list = Array.isArray(c.value) ? c.value : [c.value];

  if (isSetCondition(c.type)) {
    if (!['IN', 'NOT_IN', 'EQUALS', 'NOT_EQUALS'].includes(c.operator)) return legacy();
    return newDraft(c.type as DraftKind, {
      negate: c.operator === 'NOT_IN' || c.operator === 'NOT_EQUALS',
      values: list.map(String),
    });
  }

  switch (c.type) {
    case 'MINISTER_AGE':
      switch (c.operator) {
        case 'BETWEEN':
          return newDraft('MINISTER_AGE', { min: String(list[0]), max: String(list[1]) });
        case 'GREATER_THAN_OR_EQUAL':
          return newDraft('MINISTER_AGE', { min: String(list[0]) });
        case 'LESS_THAN_OR_EQUAL':
          return newDraft('MINISTER_AGE', { max: String(list[0]) });
        case 'EQUALS':
          return newDraft('MINISTER_AGE', { min: String(list[0]), max: String(list[0]) });
        default:
          return legacy();
      }
    case 'REGISTERED_BEFORE':
      return newDraft('REGISTERED_BEFORE', { date: String(list[0]).slice(0, 10) });
    case 'PARTICIPANT_COUNT':
      return c.operator === 'LESS_THAN' ? newDraft('PARTICIPANT_COUNT', { count: String(list[0]) }) : legacy();
    default:
      return legacy();
  }
}

/** Why a row can't be saved yet (null = complete) */
export function draftProblem(d: ConditionDraft): string | null {
  if (d.legacy) return null;
  switch (d.kind) {
    case undefined:
      return 'Elige un criterio o quita la fila';
    case 'MINISTER_AGE': {
      const hasMin = d.min.trim() !== '';
      const hasMax = d.max.trim() !== '';
      if (!hasMin && !hasMax) return 'Indica la edad mínima, la máxima o ambas';
      const min = toNum(d.min);
      const max = toNum(d.max);
      if ((hasMin && !(Number.isInteger(min) && min >= 0)) || (hasMax && !(Number.isInteger(max) && max >= 0))) {
        return 'La edad debe ser un número entero';
      }
      if (hasMin && hasMax && min > max) return 'La edad mínima no puede ser mayor que la máxima';
      return null;
    }
    case 'REGISTERED_BEFORE':
      return /^\d{4}-\d{2}-\d{2}$/.test(d.date) ? null : 'Elige la fecha límite de inscripción';
    case 'PARTICIPANT_COUNT': {
      const n = toNum(d.count);
      return d.count.trim() !== '' && Number.isInteger(n) && n >= 1 ? null : 'Indica cuántos inscritos (1 o más)';
    }
    default:
      return d.values.length > 0 ? null : 'Elige al menos una opción';
  }
}

/** Stored condition for a complete row; null when incomplete */
export function draftToCondition(d: ConditionDraft): PricingCondition | null {
  if (d.legacy) return d.legacy;
  if (draftProblem(d)) return null;
  const kind = d.kind as DraftKind;

  switch (kind) {
    case 'MINISTER_AGE': {
      const hasMin = d.min.trim() !== '';
      const hasMax = d.max.trim() !== '';
      if (hasMin && hasMax) return { type: kind, operator: 'BETWEEN', value: [Number(d.min), Number(d.max)] };
      return hasMin
        ? { type: kind, operator: 'GREATER_THAN_OR_EQUAL', value: Number(d.min) }
        : { type: kind, operator: 'LESS_THAN_OR_EQUAL', value: Number(d.max) };
    }
    case 'REGISTERED_BEFORE':
      return { type: kind, operator: 'LESS_THAN_OR_EQUAL', value: d.date };
    case 'PARTICIPANT_COUNT':
      // "first N" = fewer than N registrations ahead of this one
      return { type: kind, operator: 'LESS_THAN', value: Number(d.count) };
    default:
      return { type: kind as ConditionType, operator: d.negate ? 'NOT_IN' : 'IN', value: d.values };
  }
}

/** Complete rows as stored conditions (incomplete ones are skipped) */
export function draftsToConditions(drafts: ConditionDraft[]): PricingCondition[] {
  return drafts.map(draftToCondition).filter((c): c is PricingCondition => c !== null);
}

// ===================
// NAME SUGGESTION
// ===================

export function suggestRuleName(
  conditions: PricingCondition[],
  actionType: PricingActionType | null,
  actionValue: number | null,
  lookups?: ConditionLookups
): string {
  const who = conditions.length ? conditions.map((c) => formatCondition(c, lookups)).join(' + ') : 'Todos los participantes';
  let what = '';
  if (actionType && actionValue !== null) {
    what =
      actionType === 'PERCENTAGE_DISCOUNT'
        ? `${actionValue}% dto.`
        : actionType === 'FIXED_DISCOUNT'
          ? `Bs ${actionValue} dto.`
          : `Bs ${actionValue}`;
  }
  const name = what ? `${who} – ${what}` : who;
  return name.length > 80 ? `${name.slice(0, 77)}…` : name;
}

// ===================
// TEMPLATES
// ===================

export interface RuleTemplate {
  id: string;
  label: string;
  description: string;
  conditions: () => ConditionDraft[];
  action_type: PricingActionType;
  action_value: number | null;
}

export const RULE_TEMPLATES: RuleTemplate[] = [
  {
    id: 'early',
    label: 'Inscripción anticipada',
    description: 'Descuento a quienes se inscriban hasta una fecha',
    conditions: () => [newDraft('REGISTERED_BEFORE')],
    action_type: 'PERCENTAGE_DISCOUNT',
    action_value: 10,
  },
  {
    id: 'degree',
    label: 'Por grado ministerial',
    description: 'Precio especial según el grado',
    conditions: () => [newDraft('MINISTER_DEGREE')],
    action_type: 'PERCENTAGE_DISCOUNT',
    action_value: null,
  },
  {
    id: 'authorities',
    label: 'Autoridades',
    description: 'Superintendentes, presbíteros, etc.',
    conditions: () => [newDraft('ROLE')],
    action_type: 'PERCENTAGE_DISCOUNT',
    action_value: null,
  },
  {
    id: 'first',
    label: 'Primeros inscritos',
    description: 'Beneficio para los primeros N en inscribirse',
    conditions: () => [newDraft('PARTICIPANT_COUNT', { count: '50' })],
    action_type: 'PERCENTAGE_DISCOUNT',
    action_value: null,
  },
  {
    id: 'guests',
    label: 'Invitados y delegados',
    description: 'Precio fijo para quienes no son ministros',
    conditions: () => [newDraft('PARTICIPANT_TYPE', { values: ['GUEST', 'DELEGATE'] })],
    action_type: 'SET_PRICE',
    action_value: null,
  },
];
